'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import Link from 'next/link';
import CameraGateway from './CameraGateway';
import MRGFullWidget from './MRGFullWidget';

// ─── TILE DEFINITIONS ─────────────────────────────────────────────────────────

export interface KitbashTile {
  id: string;
  type: string;
  label: string;
  arabic: string;
  icon: string;
  color: string;
  size: 'sm' | 'md' | 'lg' | 'xl';
  description: string;
  href?: string;
  component?: string;
  tags: string[];
  isPlanted: boolean;
  isLive: boolean;
  embedCode?: string;
}

const TILE_CATALOG: Omit<KitbashTile, 'isPlanted' | 'isLive' | 'notes'>[] = [
  {
    id: 'irl-lens-camera',
    type: 'irl-camera',
    label: 'IRL Lens · Camera',
    arabic: 'عَدَسَة الكاميرا',
    icon: '📷',
    color: '#2d8a87',
    size: 'lg',
    description: 'Camera-ready IRL Lens — live fission sequences on real pictures.',
    tags: ['irl', 'camera', 'live', 'fission'],
    component: 'irl-camera',
    embedCode: '<iframe src="https://haalproject.org/user-dashboard?widget=irl-camera" width="100%" height="480" frameborder="0" allow="camera"></iframe>',
  },
  {
    id: 'irl-lens-walkthrough',
    type: 'irl',
    label: 'IRL Lens',
    arabic: 'عَدَسَة الْوَاقِع',
    icon: '◎',
    color: '#2d8a87',
    size: 'md',
    description: 'Full 8-layer IRL Lens walkthrough. Zahir → Sunnat Allah → Haqq → Amanah → Uboor.',
    href: '/inner-reality-lens',
    tags: ['irl', 'walkthrough', 'lens'],
    component: 'irl-walkthrough',
    embedCode: '<iframe src="https://haalproject.org/inner-reality-lens" width="100%" height="600" frameborder="0"></iframe>',
  },
  {
    id: 'mrg-city',
    type: 'mrg',
    label: 'Muslim Resource Guide',
    arabic: 'دَلِيل الْمَوَارِد',
    icon: '⬡',
    color: '#2d7a47',
    size: 'md',
    description: 'MRG abbreviated for your community — resources, disparities, and context for your city/ward.',
    href: '/muslim-resource-guide',
    tags: ['mrg', 'resources', 'community'],
    component: 'mrg-widget',
    embedCode: '<iframe src="https://haalproject.org/mrg-embed" width="100%" height="500" frameborder="0"></iframe>',
  },
  {
    id: 'asbab-quest',
    type: 'quest',
    label: 'Asbab Quest',
    arabic: 'قُوَّة الأَسْبَاب',
    icon: '⌘',
    color: '#D4A82A',
    size: 'lg',
    description: 'Bridged community quests — Iftar on Wheels, resource redistribution, and beneficial work in your locality.',
    tags: ['quest', 'asbab', 'community', 'iftar', 'khidmah'],
    component: 'asbab-quest',
    embedCode: '<iframe src="https://haalproject.org/asbab-quest" width="100%" height="600" frameborder="0"></iframe>',
  },
  {
    id: 'iftar-on-wheels',
    type: 'quest',
    label: 'Iftar on Wheels',
    arabic: 'إِفْطَار عَلَى الْعَجَلَات',
    icon: '🌙',
    color: '#c9a84c',
    size: 'md',
    description: 'Program and redistribute Iftar resources to those in need.',
    tags: ['iftar', 'quest', 'food', 'community', 'ramadan'],
    component: 'iftar-wheels',
  },
  {
    id: 'names-tile',
    type: 'names',
    label: 'Names of Allah',
    arabic: 'أَسْمَاء اللَّه',
    icon: '✦',
    color: '#8a8ab8',
    size: 'sm',
    description: 'The 99 Names — searchable, with IRL Lens connections.',
    href: '/names-reference',
    tags: ['names', 'asma', 'reference'],
    component: 'names-widget',
    embedCode: '<iframe src="https://haalproject.org/names-reference" width="100%" height="400" frameborder="0"></iframe>',
  },
  {
    id: 'ayat-diagram',
    type: 'ayat',
    label: 'Ayat in Creation',
    arabic: 'آيَات الْخَلْق',
    icon: '☽',
    color: '#2d7a47',
    size: 'sm',
    description: 'Quranic signs in creation — radial signs map and verse connections.',
    href: '/ayat-in-creation',
    tags: ['ayat', 'quran', 'creation'],
    component: 'ayat-widget',
  },
  {
    id: 'biome-rings',
    type: 'biome',
    label: 'Biome Ring Explorer',
    arabic: 'حَلَقَات الْبِيئَة',
    icon: '🌿',
    color: '#2d7a47',
    size: 'lg',
    description: '8 biome rings — click to expand Names, Sunnat Allah layers, and observation prompts for each biome.',
    href: '/inner-reality-lens',
    tags: ['biome', 'irl', 'mural', 'nature'],
    component: 'biome-rings',
  },
  {
    id: 'open-letter',
    type: 'letter',
    label: 'Open Letter',
    arabic: 'الرِّسَالَة الْمَفْتُوحَة',
    icon: '◈',
    color: '#c9a84c',
    size: 'md',
    description: 'The foundational open letter to imprisoned caretakers.',
    href: '/open-letter',
    tags: ['letter', 'foundation', 'caretaker'],
    component: 'open-letter',
    embedCode: '<iframe src="https://haalproject.org/open-letter" width="100%" height="600" frameborder="0"></iframe>',
  },
  {
    id: 'sl-glossary',
    type: 'glossary',
    label: 'SL Glossary',
    arabic: 'الْمُصْطَلَحَات',
    icon: '◈',
    color: '#c9a84c',
    size: 'sm',
    description: 'Spiritual Language glossary — terms, roots, and cross-links.',
    href: '/sl-glossary',
    tags: ['glossary', 'terms', 'language'],
    component: 'glossary-widget',
  },
  {
    id: 'keys',
    type: 'keys',
    label: 'Keys to Kulli Haal',
    arabic: 'مَفَاتِيح كُلِّي حَال',
    icon: '⌘',
    color: '#D4A82A',
    size: 'sm',
    description: 'The foundational keys that unlock recognition.',
    href: '/keys-to-kulli-haal',
    tags: ['keys', 'curriculum', 'foundation'],
    component: 'keys-widget',
    embedCode: '<iframe src="https://haalproject.org/keys-to-kulli-haal" width="100%" height="400" frameborder="0"></iframe>',
  },
  {
    id: 'community-feed',
    type: 'community',
    label: 'Community Feed',
    arabic: 'الْجَمَاعَة',
    icon: '⬡',
    color: '#2d7a47',
    size: 'md',
    description: 'Fisabilillah feed — community observations, quests, and connections.',
    href: '/community',
    tags: ['community', 'feed', 'fisabilillah'],
    component: 'community-widget',
  },
  {
    id: 'observation-chain',
    type: 'observation',
    label: 'Observation Chain',
    arabic: 'سِلْسِلَة الْمُلَاحَظَة',
    icon: '◎',
    color: '#2d8a87',
    size: 'md',
    description: 'Your personal observation chain — add, catalog, and archive IRL recognitions.',
    href: '/observation-chain-archive',
    tags: ['observation', 'chain', 'irl'],
    component: 'observation-widget',
  },
  {
    id: 'mrg-submit',
    type: 'mrg-submit',
    label: 'Submit to MRG',
    arabic: 'إِضَافَة لِلدَّلِيل',
    icon: '⊕',
    color: '#2d7a47',
    size: 'sm',
    description: 'Submit verified resources, disparities, or context for your ward.',
    tags: ['mrg', 'submit', 'caretaker'],
    component: 'mrg-submit',
  },
  {
    id: 'garden-workshop',
    type: 'workshop',
    label: 'Garden Workshop',
    arabic: 'وَرْشَة الْحَدِيقَة',
    icon: '⬡',
    color: '#c9a84c',
    size: 'lg',
    description: 'Active canvas for kitbashing — browse, plant, and assemble platform modules.',
    tags: ['workshop', 'kitbash', 'garden'],
    component: 'workshop',
    embedCode: '<iframe src="https://haalproject.org/garden" width="100%" height="700" frameborder="0"></iframe>',
  },
];

const SIZE_CONFIG = {
  sm: { cols: 1, rows: 1, minH: 140, label: '1×1' },
  md: { cols: 2, rows: 1, minH: 180, label: '2×1' },
  lg: { cols: 2, rows: 2, minH: 260, label: '2×2' },
  xl: { cols: 4, rows: 2, minH: 300, label: '4×2' },
};

// ─── SELF-CONTAINED WIDGET CONTENT ────────────────────────────────────────────

function IRLWalkthroughWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  const [step, setStep] = useState(0);
  const LAYERS = [
    { label: 'Zahir', arabic: 'الظَّاهِر', desc: 'The outward, visible reality — what the senses perceive.', q: 'What do you see, hear, smell, touch?' },
    { label: 'Sunnat Allah', arabic: 'سُنَّة اللَّه', desc: 'The patterns and laws woven into creation by Allah.', q: 'What pattern or system is at work here?' },
    { label: 'Haqq', arabic: 'الْحَقّ', desc: 'The truth beneath the surface — the deeper reality.', q: 'What is the deeper truth this reveals?' },
    { label: 'Amanah', arabic: 'الأَمَانَة', desc: 'The trust and responsibility this recognition calls you to.', q: 'What is your responsibility here?' },
    { label: 'Uboor', arabic: 'عُبُور', desc: 'Crossing — the recognition becomes a bridge to action.', q: 'How does this cross into your life?' },
  ];
  const layer = LAYERS[step];
  if (size === 'sm') {
    return (
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>{layer.label} · Layer {step + 1}/5</p>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10, lineHeight: 1.5 }}>{layer.q}</p>
        <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
          <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={{ flex: 1, padding: '4px', background: 'transparent', border: `1px solid ${color}30`, borderRadius: 4, color: `${color}70`, fontSize: 10, cursor: 'pointer' }}>←</button>
          <button onClick={() => setStep(s => Math.min(4, s + 1))} disabled={step === 4} style={{ flex: 1, padding: '4px', background: `${color}15`, border: `1px solid ${color}40`, borderRadius: 4, color, fontSize: 10, cursor: 'pointer' }}>→</button>
        </div>
      </div>
    );
  }
  return (
    <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, height: '100%' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {LAYERS.map((l, i) => (
          <button key={i} onClick={() => setStep(i)} style={{ flex: 1, padding: '4px 2px', background: i === step ? `${color}20` : 'transparent', border: `1px solid ${i === step ? color + '60' : color + '15'}`, borderRadius: 4, color: i === step ? color : `${color}50`, fontFamily: 'monospace', fontSize: 7, letterSpacing: '.04em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
            {l.label.split(' ')[0]}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, background: `${color}06`, border: `1px solid ${color}20`, borderRadius: 8, padding: '12px 14px' }}>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: `${color}80`, direction: 'rtl', marginBottom: 4 }}>{layer.arabic}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 11, color: '#F5F0E8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>{layer.label}</p>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>{layer.desc}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color: `${color}70`, fontStyle: 'italic' }}>"{layer.q}"</p>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={{ flex: 1, padding: '7px', background: 'transparent', border: `1px solid ${color}25`, borderRadius: 6, color: `${color}60`, fontFamily: 'monospace', fontSize: 8, cursor: 'pointer', opacity: step === 0 ? 0.3 : 1 }}>← Prev Layer</button>
        <button onClick={() => setStep(s => Math.min(4, s + 1))} disabled={step === 4} style={{ flex: 1, padding: '7px', background: `${color}15`, border: `1px solid ${color}40`, borderRadius: 6, color, fontFamily: 'monospace', fontSize: 8, cursor: 'pointer', opacity: step === 4 ? 0.3 : 1 }}>Next Layer →</button>
      </div>
    </div>
  );
}

function MRGWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  return <MRGFullWidget size={size} color={color} city="chicago" />;
}

function AsbabQuestWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  const [activeQuest, setActiveQuest] = useState(0);
  const [joined, setJoined] = useState<number[]>([]);
  const QUESTS = [
    { title: 'Iftar on Wheels', arabic: 'إِفْطَار عَلَى الْعَجَلَات', icon: '🌙', desc: 'Coordinate Iftar meal distribution to homebound community members. Link available food resources with caretakers who can deliver.', irlLink: 'Ar-Razzaq — The Provider', mrgLink: 'Food Resources · Your Ward', status: 'active', volunteers: 7, color: '#c9a84c' },
    { title: 'Ward Resource Bridge', arabic: 'جِسْر الْمَوَارِد', icon: '⬡', desc: 'Identify resource gaps in your ward using MRG disparity data, then bridge caretakers with available resources fisabilillah.', irlLink: 'Al-Adl — The Just', mrgLink: 'Disparities · Your Ward', status: 'open', volunteers: 3, color: '#2d7a47' },
    { title: 'Observation → Action', arabic: 'مُلَاحَظَة إِلَى عَمَل', icon: '◎', desc: 'Complete an IRL Lens observation in your ward, identify the Amanah it reveals, then take one concrete action fisabilillah.', irlLink: 'Amanah Layer — Layer 7', mrgLink: 'Context · Your Ward', status: 'open', volunteers: 12, color: '#2d8a87' },
  ];
  const quest = QUESTS[activeQuest];
  if (size === 'sm') {
    return (
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Asbab Quest</p>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10 }}>{quest.icon} {quest.title}</p>
        <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 9, marginTop: 2 }}>{quest.volunteers} caretakers</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
        {QUESTS.map((q, i) => (
          <button key={i} onClick={() => setActiveQuest(i)} style={{ padding: '4px 10px', borderRadius: 20, cursor: 'pointer', transition: 'all 0.2s', background: activeQuest === i ? `${q.color}20` : 'transparent', border: `1px solid ${activeQuest === i ? q.color + '60' : 'rgba(255,255,255,0.08)'}`, color: activeQuest === i ? q.color : 'rgba(212,197,169,0.4)', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.06em', textTransform: 'uppercase' }}>
            {q.icon} {q.title.split(' ')[0]}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, background: `${quest.color}06`, border: `1px solid ${quest.color}20`, borderRadius: 8, padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 18 }}>{quest.icon}</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 11, color: '#F5F0E8', fontWeight: 700 }}>{quest.title}</p>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 12, color: `${quest.color}70` }}>{quest.arabic}</p>
          </div>
          <span style={{ fontFamily: 'monospace', fontSize: 7, padding: '3px 8px', borderRadius: 10, background: quest.status === 'active' ? `${quest.color}20` : 'rgba(255,255,255,0.04)', border: `1px solid ${quest.status === 'active' ? quest.color + '40' : 'rgba(255,255,255,0.08)'}`, color: quest.status === 'active' ? quest.color : 'rgba(212,197,169,0.3)', textTransform: 'uppercase', letterSpacing: '.08em', flexShrink: 0 }}>
            {quest.status}
          </span>
        </div>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>{quest.desc}</p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 8, padding: '3px 8px', borderRadius: 4, background: 'rgba(45,138,135,0.1)', border: '1px solid rgba(45,138,135,0.2)', color: '#2d8a87' }}>IRL: {quest.irlLink}</span>
          <span style={{ fontFamily: 'monospace', fontSize: 8, padding: '3px 8px', borderRadius: 4, background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.2)', color: '#2d7a47' }}>MRG: {quest.mrgLink}</span>
        </div>
        <button
          onClick={() => setJoined(j => j.includes(activeQuest) ? j.filter(x => x !== activeQuest) : [...j, activeQuest])}
          style={{ padding: '7px 14px', background: joined.includes(activeQuest) ? `${quest.color}25` : `${quest.color}12`, border: `1px solid ${quest.color}${joined.includes(activeQuest) ? '60' : '35'}`, borderRadius: 6, color: quest.color, fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}
        >
          {joined.includes(activeQuest) ? '✓ Joined · ' + (quest.volunteers + 1) + ' caretakers' : '⊕ Join Quest · ' + quest.volunteers + ' caretakers'}
        </button>
      </div>
      <Link href="/asbab-quest" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: `${color}70`, textDecoration: 'none' }}>
        Full Asbab Quest Interface →
      </Link>
    </div>
  );
}

function NamesWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<number | null>(null);
  const NAMES = [
    { num: 1, name: 'Ar-Rahman', arabic: 'الرَّحْمَن', meaning: 'The Most Gracious', irl: 'Every act of mercy in creation reflects this Name.' },
    { num: 2, name: 'Ar-Rahim', arabic: 'الرَّحِيم', meaning: 'The Most Merciful', irl: 'Sustained mercy — the ongoing care woven into existence.' },
    { num: 8, name: 'Al-Hafiz', arabic: 'الْحَفِيظ', meaning: 'The Preserver', irl: 'The systems that maintain balance in ecosystems and communities.' },
    { num: 17, name: 'Al-Fattah', arabic: 'الْفَتَّاح', meaning: 'The Opener', irl: 'Every door that opens — in nature, in community, in the heart.' },
    { num: 42, name: 'Al-Adl', arabic: 'الْعَدْل', meaning: 'The Just', irl: 'The balance in creation — ecological, social, spiritual.' },
    { num: 18, name: 'Al-Alim', arabic: 'الْعَلِيم', meaning: 'The All-Knowing', irl: 'The intelligence embedded in every system of creation.' },
    { num: 40, name: 'Al-Khaliq', arabic: 'الْخَالِق', meaning: 'The Creator', irl: 'The origination of form — from seed to community to cosmos.' },
    { num: 12, name: 'Ar-Razzaq', arabic: 'الرَّزَّاق', meaning: 'The Provider', irl: 'The flow of sustenance through every ecosystem and community.' },
  ];
  const filtered = search ? NAMES.filter(n => n.name.toLowerCase().includes(search.toLowerCase()) || n.meaning.toLowerCase().includes(search.toLowerCase())) : NAMES;
  if (size === 'sm') {
    const n = NAMES[Math.floor(Date.now() / 86400000) % NAMES.length];
    return (
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${color}60`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Name of the Day</p>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color, direction: 'rtl', marginBottom: 2 }}>{n.arabic}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#F5F0E8', fontWeight: 700 }}>{n.name}</p>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9 }}>{n.meaning}</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search Names..." style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${color}25`, borderRadius: 6, padding: '6px 10px', color: '#F5F0E8', fontSize: 10, fontFamily: 'monospace', outline: 'none' }} />
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {filtered.slice(0, 5).map(n => (
          <button key={n.num} onClick={() => setSelected(selected === n.num ? null : n.num)} style={{ textAlign: 'left', padding: '8px 10px', background: selected === n.num ? `${color}12` : `${color}04`, border: `1px solid ${selected === n.num ? color + '40' : color + '15'}`, borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${color}80` }}>{n.arabic}</span>
              <div style={{ flex: 1 }}>
                <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#F5F0E8', fontWeight: 700 }}>{n.name}</p>
                <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 9 }}>{n.meaning}</p>
              </div>
              <span style={{ fontFamily: 'monospace', fontSize: 8, color: `${color}50` }}>#{n.num}</span>
            </div>
            {selected === n.num && (
              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10, lineHeight: 1.6, marginTop: 6, paddingTop: 6, borderTop: `1px solid ${color}15` }}>{n.irl}</p>
            )}
          </button>
        ))}
      </div>
      <Link href="/names-reference" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: `${color}70`, textDecoration: 'none' }}>
        All 99 Names →
      </Link>
    </div>
  );
}

function ObservationWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  const [obs, setObs] = useState('');
  const [saved, setSaved] = useState<string[]>([]);
  const [layer, setLayer] = useState('Zahir');
  const LAYERS = ['Zahir', 'Sunnat Allah', 'Haqq', 'Amanah', 'Uboor'];
  if (size === 'sm') {
    return (
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${color}60`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 6 }}>Quick Observation</p>
        <input value={obs} onChange={e => setObs(e.target.value)} placeholder="What do you observe?" style={{ width: '100%', background: 'rgba(255,255,255,0.04)', border: `1px solid ${color}25`, borderRadius: 4, padding: '5px 8px', color: '#F5F0E8', fontSize: 10, fontFamily: 'monospace', outline: 'none', boxSizing: 'border-box' }} />
        <button onClick={() => { if (obs.trim()) { setSaved(s => [obs, ...s].slice(0, 5)); setObs(''); } }} style={{ marginTop: 6, width: '100%', padding: '5px', background: `${color}15`, border: `1px solid ${color}35`, borderRadius: 4, color, fontFamily: 'monospace', fontSize: 8, cursor: 'pointer' }}>
          + Add
        </button>
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {LAYERS.map(l => (
          <button key={l} onClick={() => setLayer(l)} style={{ padding: '3px 8px', background: layer === l ? `${color}20` : 'transparent', border: `1px solid ${layer === l ? color + '50' : color + '15'}`, borderRadius: 4, color: layer === l ? color : `${color}50`, fontFamily: 'monospace', fontSize: 7, letterSpacing: '.04em', textTransform: 'uppercase', cursor: 'pointer' }}>
            {l.split(' ')[0]}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input value={obs} onChange={e => setObs(e.target.value)} placeholder={`${layer} observation...`} style={{ flex: 1, background: 'rgba(255,255,255,0.04)', border: `1px solid ${color}25`, borderRadius: 6, padding: '7px 10px', color: '#F5F0E8', fontSize: 10, fontFamily: 'monospace', outline: 'none' }} />
        <button onClick={() => { if (obs.trim()) { setSaved(s => [`[${layer}] ${obs}`, ...s].slice(0, 6)); setObs(''); } }} style={{ padding: '7px 12px', background: `${color}15`, border: `1px solid ${color}35`, borderRadius: 6, color, fontFamily: 'monospace', fontSize: 9, cursor: 'pointer' }}>⊕</button>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {saved.length === 0 && <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 11, fontStyle: 'italic', textAlign: 'center', paddingTop: 16 }}>No observations yet — begin your chain.</p>}
        {saved.map((s, i) => (
          <div key={i} style={{ padding: '7px 10px', background: `${color}06`, border: `1px solid ${color}15`, borderRadius: 6, marginBottom: 5 }}>
            <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 10, lineHeight: 1.5 }}>{s}</p>
          </div>
        ))}
      </div>
      <Link href="/observation-chain-archive" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: `${color}70`, textDecoration: 'none' }}>
        Full Archive →
      </Link>
    </div>
  );
}

function KeysWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  const [active, setActive] = useState(0);
  const KEYS = [
    { key: 'Is it?', arabic: 'هَلْ هُوَ؟', desc: 'The first question — does this thing exist? Acknowledge the Ayah before you.', example: 'Is this leaf an Ayah? Yes.' },
    { key: 'What is it?', arabic: 'مَا هُوَ؟', desc: 'The second question — what is the nature of this thing? Examine its form and function.', example: 'What is this leaf? A system of photosynthesis, a sign of Ar-Razzaq.' },
    { key: 'When is it?', arabic: 'مَتَى هُوَ؟', desc: 'The third question — when does this thing occur? Understand its timing and context.', example: 'When is this leaf? In autumn, in the cycle of return.' },
  ];
  const k = KEYS[active];
  if (size === 'sm') {
    return (
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>{k.key}</p>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${color}70`, direction: 'rtl' }}>{k.arabic}</p>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9, marginTop: 4 }}>{k.desc.slice(0, 60)}...</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
      <div style={{ display: 'flex', gap: 5 }}>
        {KEYS.map((k, i) => (
          <button key={i} onClick={() => setActive(i)} style={{ flex: 1, padding: '5px 4px', background: active === i ? `${color}20` : 'transparent', border: `1px solid ${active === i ? color + '50' : color + '15'}`, borderRadius: 5, color: active === i ? color : `${color}50`, fontFamily: 'monospace', fontSize: 7, letterSpacing: '.04em', textTransform: 'uppercase', cursor: 'pointer' }}>
            Key {i + 1}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, background: `${color}06`, border: `1px solid ${color}20`, borderRadius: 8, padding: '12px 14px' }}>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: `${color}80`, direction: 'rtl', marginBottom: 4 }}>{k.arabic}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 12, color: '#F5F0E8', fontWeight: 700, marginBottom: 6 }}>{k.key}</p>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>{k.desc}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color: `${color}70`, fontStyle: 'italic' }}>e.g. {k.example}</p>
      </div>
      <Link href="/keys-to-kulli-haal" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: `${color}70`, textDecoration: 'none' }}>
        Full Keys Framework →
      </Link>
    </div>
  );
}

function GlossaryWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const TERMS = [
    { term: 'Mata', arabic: 'مَاطَا', def: 'The first encounter — seeing with fresh eyes. The moment of initial recognition before analysis.', root: 'From the concept of first sight in the framework.' },
    { term: 'Uboor', arabic: 'عُبُور', def: 'Crossing — the journey becomes the path itself. Recognition that transforms into action and service.', root: 'From the Arabic root meaning to cross or traverse.' },
    { term: 'Amanah', arabic: 'الأَمَانَة', def: 'Trust, responsibility, stewardship. The sacred charge placed upon the caretaker by recognition.', root: 'From the root أ-م-ن meaning trust and security.' },
    { term: 'Islah', arabic: 'إِصْلَاح', def: 'Reform, restoration, making right. The active work of beneficial change in self and community.', root: 'From the root ص-ل-ح meaning righteousness and goodness.' },
    { term: 'Haal', arabic: 'حَال', def: 'State, condition, present moment. The current reality — inner and outer — that the caretaker inhabits.', root: 'From the root ح-و-ل meaning state or condition.' },
    { term: 'Fission', arabic: 'الانشطار', def: 'The IRL Lens process of splitting open a moment of observation to reveal its deeper layers and connections.', root: 'Framework term for the multi-layer analysis process.' },
  ];
  const filtered = search ? TERMS.filter(t => t.term.toLowerCase().includes(search.toLowerCase()) || t.def.toLowerCase().includes(search.toLowerCase())) : TERMS;
  if (size === 'sm') {
    const t = TERMS[0];
    return (
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color, direction: 'rtl', marginBottom: 2 }}>{t.arabic}</p>
        <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#F5F0E8', fontWeight: 700 }}>{t.term}</p>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 9, marginTop: 2 }}>{t.def.slice(0, 60)}...</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search terms..." style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${color}25`, borderRadius: 6, padding: '6px 10px', color: '#F5F0E8', fontSize: 10, fontFamily: 'monospace', outline: 'none' }} />
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {filtered.slice(0, 5).map(t => (
          <button key={t.term} onClick={() => setExpanded(expanded === t.term ? null : t.term)} style={{ textAlign: 'left', padding: '8px 10px', background: expanded === t.term ? `${color}10` : `${color}04`, border: `1px solid ${expanded === t.term ? color + '35' : color + '12'}`, borderRadius: 6, cursor: 'pointer', transition: 'all 0.2s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: "'Amiri', serif", fontSize: 14, color: `${color}80` }}>{t.arabic}</span>
              <p style={{ fontFamily: 'monospace', fontSize: 10, color: '#F5F0E8', fontWeight: 700, flex: 1 }}>{t.term}</p>
              <span style={{ color: `${color}50`, fontSize: 10 }}>{expanded === t.term ? '▲' : '▼'}</span>
            </div>
            {expanded === t.term && (
              <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${color}15` }}>
                <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 10, lineHeight: 1.6, marginBottom: 4 }}>{t.def}</p>
                <p style={{ color: 'rgba(212,197,169,0.3)', fontSize: 9, fontStyle: 'italic' }}>{t.root}</p>
              </div>
            )}
          </button>
        ))}
      </div>
      <Link href="/sl-glossary" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: `${color}70`, textDecoration: 'none' }}>
        Full SL Glossary →
      </Link>
    </div>
  );
}

function LetterWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  const QUOTES = [
    { text: 'We aren\'t some spontaneous occurrence. Reflect on the perfect systems and design of creation around us.', section: 'Opening' },
    { text: 'The caretaker is one who tends — to the self, to the community, to the earth, to the trust placed in them.', section: 'Caretaker' },
    { text: 'Islah begins with the recognition that something needs tending. The first act of reform is seeing clearly.', section: 'Islah' },
  ];
  const [idx, setIdx] = useState(0);
  const q = QUOTES[idx];
  if (size === 'sm') {
    return (
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${color}60`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>Open Letter · {q.section}</p>
        <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 10, lineHeight: 1.5, fontStyle: 'italic' }}>"{q.text.slice(0, 80)}..."</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, height: '100%' }}>
      <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${color}60`, letterSpacing: '.12em', textTransform: 'uppercase' }}>Open Letter to Imprisoned Caretakers · {q.section}</p>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${color}06`, border: `1px solid ${color}15`, borderRadius: 8, padding: '16px 18px' }}>
        <p style={{ color: 'rgba(212,197,169,0.8)', fontSize: 13, lineHeight: 1.8, fontStyle: 'italic', textAlign: 'center' }}>"{q.text}"</p>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={() => setIdx(i => (i - 1 + QUOTES.length) % QUOTES.length)} style={{ padding: '5px 10px', background: 'transparent', border: `1px solid ${color}25`, borderRadius: 5, color: `${color}60`, fontFamily: 'monospace', fontSize: 9, cursor: 'pointer' }}>←</button>
        <span style={{ flex: 1, textAlign: 'center', fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>{idx + 1} / {QUOTES.length}</span>
        <button onClick={() => setIdx(i => (i + 1) % QUOTES.length)} style={{ padding: '5px 10px', background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 5, color, fontFamily: 'monospace', fontSize: 9, cursor: 'pointer' }}>→</button>
        <Link href="/open-letter" style={{ padding: '5px 10px', background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 5, color, fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', textDecoration: 'none' }}>Read →</Link>
      </div>
    </div>
  );
}

function AyatWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  const AYAT = [
    { ref: '41:53', arabic: 'سَنُرِيهِمْ آيَاتِنَا فِي الْآفَاقِ وَفِي أَنفُسِهِمْ', translation: 'We will show them Our signs in the horizons and within themselves until it becomes clear to them that it is the truth.' },
    { ref: '6:99', arabic: 'وَهُوَ الَّذِي أَنزَلَ مِنَ السَّمَاءِ مَاءً', translation: 'It is He who sends down water from the sky; from it We bring forth the growth of everything.' },
    { ref: '87:1-3', arabic: 'سَبِّحِ اسْمَ رَبِّكَ الْأَعْلَى الَّذِي خَلَقَ فَسَوَّىٰ', translation: 'Glorify the name of your Lord, the Most High, Who created and proportioned.' },
  ];
  const [idx, setIdx] = useState(0);
  const a = AYAT[idx];
  if (size === 'sm') {
    return (
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${color}60`, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}>{a.ref}</p>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color, direction: 'rtl', lineHeight: 1.8 }}>{a.arabic.slice(0, 40)}...</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, height: '100%' }}>
      <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${color}60`, letterSpacing: '.12em', textTransform: 'uppercase' }}>Ayat in Creation · {a.ref}</p>
      <div style={{ flex: 1, background: `${color}06`, border: `1px solid ${color}15`, borderRadius: 8, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color, direction: 'rtl', lineHeight: 2, textAlign: 'right' }}>{a.arabic}</p>
        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.7, fontStyle: 'italic' }}>{a.translation}</p>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button onClick={() => setIdx(i => (i - 1 + AYAT.length) % AYAT.length)} style={{ padding: '5px 10px', background: 'transparent', border: `1px solid ${color}25`, borderRadius: 5, color: `${color}60`, fontFamily: 'monospace', fontSize: 9, cursor: 'pointer' }}>←</button>
        <span style={{ flex: 1, textAlign: 'center', fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)' }}>{idx + 1} / {AYAT.length}</span>
        <button onClick={() => setIdx(i => (i + 1) % AYAT.length)} style={{ padding: '5px 10px', background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 5, color, fontFamily: 'monospace', fontSize: 9, cursor: 'pointer' }}>→</button>
        <Link href="/ayat-in-creation" style={{ padding: '5px 10px', background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 5, color, fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', textDecoration: 'none' }}>Explore →</Link>
      </div>
    </div>
  );
}

function CameraWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  if (size === 'sm') {
    return (
      <div style={{ padding: '10px 12px', textAlign: 'center' }}>
        <div style={{ fontSize: 24, marginBottom: 4 }}>📷</div>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${color}70`, letterSpacing: '.08em', textTransform: 'uppercase' }}>IRL Camera</p>
        <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 9, marginTop: 2 }}>Live fission</p>
      </div>
    );
  }
  return (
    <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, height: '100%' }}>
      <p style={{ fontFamily: 'monospace', fontSize: 8, color: `${color}60`, letterSpacing: '.12em', textTransform: 'uppercase' }}>IRL Lens · Camera Gateway</p>
      <div style={{ flex: 1, background: `${color}08`, border: `1px solid ${color}20`, borderRadius: 8, padding: '16px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        <div style={{ fontSize: 36 }}>📷</div>
        <p style={{ fontFamily: 'monospace', fontSize: 10, color: `${color}80`, letterSpacing: '.1em', textTransform: 'uppercase' }}>Live Fission Sequences</p>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, lineHeight: 1.6, maxWidth: 260 }}>
          Attach your camera to run the IRL Lens on real pictures — the framework unfolding on what you see, linked to MRG context cards.
        </p>
        <Link href="/user-dashboard?widget=camera" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: `${color}15`, border: `1px solid ${color}40`, borderRadius: 6, color, fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none', marginTop: 4 }}>
          📷 Open Camera Gateway →
        </Link>
      </div>
      <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.25)', textAlign: 'center' }}>
        Powered by Anthropic Vision · Linked to IRL Lens + MRG
      </p>
    </div>
  );
}

function MRGSubmitWidget({ size, color }: { size: KitbashTile['size']; color: string }) {
  const [type, setType] = useState<'resource' | 'disparity' | 'context'>('resource');
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);
  if (size === 'sm') {
    return (
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Submit to MRG</p>
        <Link href="/mrg-embed/collection" style={{ fontFamily: 'monospace', fontSize: 8, color: `${color}70`, textDecoration: 'none' }}>⊕ Add resource →</Link>
      </div>
    );
  }
  if (submitted) {
    return (
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, height: '100%' }}>
        <div style={{ fontSize: 28 }}>✓</div>
        <p style={{ fontFamily: 'monospace', fontSize: 11, color, letterSpacing: '.08em', textTransform: 'uppercase' }}>Submitted for Review</p>
        <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, textAlign: 'center', lineHeight: 1.6 }}>Your submission will be reviewed by a ward steward before publishing to the MRG collection.</p>
        <button onClick={() => { setSubmitted(false); setText(''); }} style={{ padding: '7px 14px', background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 6, color, fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>Submit Another</button>
      </div>
    );
  }
  return (
    <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {(['resource', 'disparity', 'context'] as const).map(t => (
          <button key={t} onClick={() => setType(t)} style={{ flex: 1, padding: '4px', background: type === t ? `${color}20` : 'transparent', border: `1px solid ${type === t ? color + '50' : color + '15'}`, borderRadius: 4, color: type === t ? color : 'rgba(212,197,169,0.4)', fontFamily: 'monospace', fontSize: 7, letterSpacing: '.04em', textTransform: 'uppercase', cursor: 'pointer' }}>
            {t.slice(0, 4)}
          </button>
        ))}
      </div>
      <textarea value={text} onChange={e => setText(e.target.value)} placeholder={`Describe the ${type} for your ward...`} rows={3} style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${color}25`, borderRadius: 6, padding: '8px 10px', color: '#F5F0E8', fontSize: 10, fontFamily: 'monospace', outline: 'none', resize: 'none', flex: 1 }} />
      <button onClick={() => { if (text.trim()) setSubmitted(true); }} style={{ padding: '8px', background: `${color}15`, border: `1px solid ${color}40`, borderRadius: 6, color, fontFamily: 'monospace', fontSize: 9, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
        ⊕ Submit {type} for Review
      </button>
    </div>
  );
}

// ─── EMBED PANEL ──────────────────────────────────────────────────────────────

function EmbedPanel({ tile, onClose }: { tile: KitbashTile; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const embedUrl = `https://haalproject.org/user-dashboard?widget=${tile.id}`;
  const iframeCode = tile.embedCode || `<iframe src="${embedUrl}" width="100%" height="500" frameborder="0" loading="lazy" title="${tile.label} — The Haal Project"></iframe>`;
  const scriptCode = `<div id="haal-${tile.id}"></div>\n<script src="https://haalproject.org/embed.js" data-widget="${tile.id}" data-target="haal-${tile.id}"></script>`;

  const copy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div style={{ position: 'absolute', inset: 0, background: 'rgba(6,13,18,0.97)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 10, zIndex: 20, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, overflow: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.7)', letterSpacing: '.12em', textTransform: 'uppercase' }}>Embed / Share · {tile.label}</p>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'rgba(212,197,169,0.4)', fontSize: 14, cursor: 'pointer' }}>✕</button>
      </div>
      <div>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>iframe Embed</p>
        <div style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '8px 10px', marginBottom: 4 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.5)', wordBreak: 'break-all', lineHeight: 1.6 }}>{iframeCode}</p>
        </div>
        <button onClick={() => copy(iframeCode)} style={{ padding: '5px 12px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 4, color: '#c9a84c', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
          {copied ? '✓ Copied' : 'Copy iframe'}
        </button>
      </div>
      <div>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Script Embed</p>
        <div style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '8px 10px', marginBottom: 4 }}>
          <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.5)', wordBreak: 'break-all', lineHeight: 1.6 }}>{scriptCode}</p>
        </div>
        <button onClick={() => copy(scriptCode)} style={{ padding: '5px 12px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 4, color: '#c9a84c', fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', cursor: 'pointer' }}>
          Copy Script
        </button>
      </div>
      <div>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.4)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 4 }}>Direct Link</p>
        <div style={{ display: 'flex', gap: 6 }}>
          <div style={{ flex: 1, background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, padding: '6px 10px' }}>
            <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.5)', wordBreak: 'break-all' }}>{embedUrl}</p>
          </div>
          <button onClick={() => copy(embedUrl)} style={{ padding: '5px 10px', background: 'rgba(45,122,71,0.1)', border: '1px solid rgba(45,122,71,0.3)', borderRadius: 4, color: '#2d7a47', fontFamily: 'monospace', fontSize: 8, cursor: 'pointer', flexShrink: 0 }}>Copy</button>
        </div>
      </div>
      <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.25)', lineHeight: 1.6 }}>
        Embed this widget on any website — masjid learning pages, community portals, activist toolkits. Links back to haalproject.org.
      </p>
    </div>
  );
}

// ─── TILE CARD ────────────────────────────────────────────────────────────────

function TileCard({
  tile, editMode, onRemove, onResize, onDragStart, onDragOver, onDrop, isDragging, isDragOver,
}: {
  tile: KitbashTile; editMode: boolean;
  onRemove: (id: string) => void; onResize: (id: string, size: KitbashTile['size']) => void;
  onDragStart: (id: string) => void; onDragOver: (e: React.DragEvent, id: string) => void;
  onDrop: (targetId: string) => void; isDragging: boolean; isDragOver: boolean;
}) {
  const sizeConf = SIZE_CONFIG[tile.size];
  const [showEmbed, setShowEmbed] = useState(false);
  const gridColSpan = tile.size === 'xl' ? 4 : tile.size === 'lg' ? 2 : tile.size === 'md' ? 2 : 1;

  const renderContent = () => {
    switch (tile.component) {
      case 'irl-camera': return tile.size === 'lg' || tile.size === 'xl' ? <CameraGateway /> : <CameraWidget size={tile.size} color={tile.color} />;
      case 'irl-walkthrough': return <IRLWalkthroughWidget size={tile.size} color={tile.color} />;
      case 'mrg-widget': return <MRGWidget size={tile.size} color={tile.color} />;
      case 'asbab-quest': return <AsbabQuestWidget size={tile.size} color={tile.color} />;
      case 'iftar-wheels': return <AsbabQuestWidget size={tile.size} color={tile.color} />;
      case 'names-widget': return <NamesWidget size={tile.size} color={tile.color} />;
      case 'ayat-widget': return <AyatWidget size={tile.size} color={tile.color} />;
      case 'observation-widget': return <ObservationWidget size={tile.size} color={tile.color} />;
      case 'keys-widget': return <KeysWidget size={tile.size} color={tile.color} />;
      case 'glossary-widget': return <GlossaryWidget size={tile.size} color={tile.color} />;
      case 'letter-widget': return <LetterWidget size={tile.size} color={tile.color} />;
      case 'mrg-submit': return <MRGSubmitWidget size={tile.size} color={tile.color} />;
      case 'biome-rings':
        return (
          <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, flex: 1 }}>
              {['🌿 Grasslands', '💧 Wetlands', '🌊 River', '🌲 Forest', '⛰️ Mountain', '🏜️ Desert', '🏙️ Urban', '☁️ Sky'].map(b => (
                <Link key={b} href="/inner-reality-lens" style={{ padding: '6px 10px', background: `${tile.color}08`, border: `1px solid ${tile.color}20`, borderRadius: 6, color: tile.color, fontFamily: 'monospace', fontSize: 9, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>{b}</Link>
              ))}
            </div>
            <Link href="/inner-reality-lens" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: `${tile.color}70`, textDecoration: 'none' }}>Full Biome Explorer →</Link>
          </div>
        );
      case 'community-widget':
        return (
          <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6, flex: 1 }}>{tile.description}</p>
            <Link href="/community" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: `${tile.color}12`, border: `1px solid ${tile.color}35`, borderRadius: 6, color: tile.color, fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', textDecoration: 'none' }}>
              ⬡ Community Feed →
            </Link>
          </div>
        );
      case 'workshop':
        return (
          <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6, flex: 1 }}>{tile.description}</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <Link href="/open-letter" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '7px', background: `${tile.color}12`, border: `1px solid ${tile.color}35`, borderRadius: 6, color: tile.color, fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', textDecoration: 'none' }}>
                Workshop →
              </Link>
            </div>
          </div>
        );
      default:
        return (
          <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8, height: '100%' }}>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 11, lineHeight: 1.6, flex: 1 }}>{tile.description}</p>
            {tile.href && (
              <Link href={tile.href} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', background: `${tile.color}12`, border: `1px solid ${tile.color}35`, borderRadius: 6, color: tile.color, fontFamily: 'monospace', fontSize: 8, letterSpacing: '.08em', textTransform: 'uppercase', textDecoration: 'none' }}>
                Open →
              </Link>
            )}
          </div>
        );
    }
  };

  return (
    <div
      draggable={editMode}
      onDragStart={() => onDragStart(tile.id)}
      onDragOver={e => onDragOver(e, tile.id)}
      onDrop={() => onDrop(tile.id)}
      style={{
        gridColumn: `span ${gridColSpan}`,
        minHeight: sizeConf.minH,
        background: isDragOver ? `${tile.color}15` : 'rgba(255,255,255,0.02)',
        border: `1px solid ${isDragOver ? tile.color + '60' : isDragging ? tile.color + '40' : tile.color + '20'}`,
        borderTop: `3px solid ${tile.color}`,
        borderRadius: 10,
        overflow: 'hidden',
        transition: 'all 0.2s',
        opacity: isDragging ? 0.5 : 1,
        cursor: editMode ? 'grab' : 'default',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {/* Tile header */}
      <div style={{ padding: '8px 12px 6px', borderBottom: `1px solid ${tile.color}15`, background: `${tile.color}06`, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <span style={{ fontSize: 14, flexShrink: 0 }}>{tile.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontFamily: 'monospace', fontSize: tile.size === 'sm' ? 8 : 9, color: '#F5F0E8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tile.label}</p>
          {tile.size !== 'sm' && <p style={{ fontFamily: "'Amiri', serif", fontSize: 10, color: `${tile.color}60` }}>{tile.arabic}</p>}
        </div>
        {tile.isLive && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#2d7a47' }} />
            <span style={{ fontFamily: 'monospace', fontSize: 6, color: '#2d7a47', letterSpacing: '.08em', textTransform: 'uppercase' }}>LIVE</span>
          </div>
        )}
        {/* Embed button */}
        {!editMode && (tile.embedCode || tile.href) && (
          <button onClick={() => setShowEmbed(v => !v)} style={{ background: 'transparent', border: `1px solid ${tile.color}20`, borderRadius: 4, padding: '2px 6px', color: `${tile.color}60`, fontFamily: 'monospace', fontSize: 7, cursor: 'pointer', flexShrink: 0, letterSpacing: '.04em' }}>
            {'</>'}
          </button>
        )}
        {editMode && (
          <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
            <select value={tile.size} onChange={e => onResize(tile.id, e.target.value as KitbashTile['size'])} onClick={e => e.stopPropagation()} style={{ background: 'rgba(6,13,18,0.9)', border: `1px solid ${tile.color}30`, borderRadius: 4, padding: '2px 4px', color: tile.color, fontSize: 7, fontFamily: 'monospace', cursor: 'pointer' }}>
              {Object.entries(SIZE_CONFIG).map(([s, c]) => <option key={s} value={s}>{c.label}</option>)}
            </select>
            <button onClick={() => setEditMode(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: editMode ? 'rgba(45,122,71,0.15)' : 'rgba(255,255,255,0.03)', border: `1px solid ${editMode ? 'rgba(45,122,71,0.5)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 8, color: editMode ? '#2d7a47' : 'rgba(212,197,169,0.5)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
              {editMode ? '✓ Done' : '⌘ Edit'}
            </button>
            <Link href="/ward-steward" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(138,138,184,0.08)', border: '1px solid rgba(138,138,184,0.25)', borderRadius: 8, color: '#8a8ab8', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none', transition: 'all 0.2s' }}>
              ⬡ Ward Steward
            </Link>
          </div>
        )}
      </div>

      {/* Tile content */}
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {renderContent()}
        {showEmbed && <EmbedPanel tile={tile} onClose={() => setShowEmbed(false)} />}
      </div>

      {/* Tags footer */}
      <div style={{ padding: '4px 10px 6px', display: 'flex', gap: 3, flexWrap: 'wrap', borderTop: `1px solid ${tile.color}08`, flexShrink: 0 }}>
        {tile.tags.slice(0, tile.size === 'sm' ? 2 : 3).map(tag => (
          <span key={tag} style={{ fontFamily: 'monospace', fontSize: 6, padding: '1px 5px', borderRadius: 10, background: `${tile.color}08`, border: `1px solid ${tile.color}12`, color: `${tile.color}60`, letterSpacing: '.06em', textTransform: 'uppercase' }}>{tag}</span>
        ))}
      </div>
    </div>
  );
}

// ─── MAIN KITBASH CANVAS ──────────────────────────────────────────────────────

const DEFAULT_PLANTED = ['irl-lens-camera', 'asbab-quest', 'mrg-city', 'irl-lens-walkthrough', 'names-tile', 'observation-chain', 'keys', 'ayat-diagram'];

export default function KitbashGardenCanvas() {
  const [plantedIds, setPlantedIds] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('haal_kitbash_planted');
      if (saved) try { return JSON.parse(saved); } catch { return DEFAULT_PLANTED; }
    }
    return DEFAULT_PLANTED;
  });
  const [tileSizes, setTileSizes] = useState<Record<string, KitbashTile['size']>>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('haal_kitbash_sizes');
      if (saved) try { return JSON.parse(saved); } catch { return {}; }
    }
    return {};
  });
  const [editMode, setEditMode] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const plantedTiles: KitbashTile[] = plantedIds
    .map(id => {
      const def = TILE_CATALOG.find(t => t.id === id);
      if (!def) return null;
      return { ...def, isPlanted: true, isLive: ['irl-lens-walkthrough', 'mrg-city', 'community-feed', 'observation-chain'].includes(id), size: tileSizes[id] || def.size };
    })
    .filter(Boolean) as KitbashTile[];

  const availableTiles = TILE_CATALOG.filter(t => !plantedIds.includes(t.id));
  const filteredAvailable = searchQuery
    ? availableTiles.filter(t => t.label.toLowerCase().includes(searchQuery.toLowerCase()) || t.tags.some(tag => tag.includes(searchQuery.toLowerCase())))
    : availableTiles;

  function savePlanted(ids: string[]) {
    setPlantedIds(ids);
    if (typeof window !== 'undefined') localStorage.setItem('haal_kitbash_planted', JSON.stringify(ids));
  }
  function saveSizes(sizes: Record<string, KitbashTile['size']>) {
    setTileSizes(sizes);
    if (typeof window !== 'undefined') localStorage.setItem('haal_kitbash_sizes', JSON.stringify(sizes));
  }
  function plantTile(id: string) { savePlanted([...plantedIds, id]); }
  function removeTile(id: string) { savePlanted(plantedIds.filter(p => p !== id)); }
  function resizeTile(id: string, size: KitbashTile['size']) { saveSizes({ ...tileSizes, [id]: size }); }
  function handleDragStart(id: string) { setDragging(id); }
  function handleDragOver(e: React.DragEvent, id: string) { e.preventDefault(); setDragOver(id); }
  function handleDrop(targetId: string) {
    if (!dragging || dragging === targetId) return;
    const newIds = [...plantedIds];
    const fromIdx = newIds.indexOf(dragging);
    const toIdx = newIds.indexOf(targetId);
    if (fromIdx !== -1 && toIdx !== -1) { newIds.splice(fromIdx, 1); newIds.splice(toIdx, 0, dragging); savePlanted(newIds); }
    setDragging(null); setDragOver(null);
  }

  return (
    <div>
      {/* Canvas header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.15em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.5)', marginBottom: 4 }}>⬡ Kitbash Canvas — Modular Garden Plot</div>
          <h2 style={{ fontFamily: 'monospace', fontSize: 15, color: '#F5F0E8', textTransform: 'uppercase', letterSpacing: '.04em' }}>My Garden</h2>
          <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 11, marginTop: 2 }}>
            {plantedTiles.length} tiles planted · Each tile is a self-contained tool · Resize, rearrange, embed anywhere
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={() => setShowCatalog(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: showCatalog ? 'rgba(45,122,71,0.15)' : 'rgba(255,255,255,0.03)', border: `1px solid ${showCatalog ? 'rgba(45,122,71,0.5)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 8, color: showCatalog ? '#2d7a47' : 'rgba(212,197,169,0.5)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
            ⊕ Plant Tool
          </button>
          <button onClick={() => setEditMode(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: editMode ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.03)', border: `1px solid ${editMode ? 'rgba(201,168,76,0.5)' : 'rgba(255,255,255,0.1)'}`, borderRadius: 8, color: editMode ? '#c9a84c' : 'rgba(212,197,169,0.5)', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer', transition: 'all 0.2s' }}>
            {editMode ? '✓ Done' : '⌘ Edit'}
          </button>
          <Link href="/ward-steward" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(138,138,184,0.08)', border: '1px solid rgba(138,138,184,0.25)', borderRadius: 8, color: '#8a8ab8', fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', textDecoration: 'none', transition: 'all 0.2s' }}>
            ⬡ Ward Steward
          </Link>
        </div>
      </div>

      {/* Catalog drawer */}
      {showCatalog && (
        <div style={{ background: 'rgba(6,13,18,0.95)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 10, padding: '16px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <p style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'rgba(201,168,76,0.6)' }}>
              Tool Catalog — {filteredAvailable.length} available
            </p>
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search tools..." style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 6, padding: '6px 10px', color: '#F5F0E8', fontSize: 11, fontFamily: 'monospace', outline: 'none', width: 160 }} />
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {filteredAvailable.map(tile => (
              <button key={tile.id} onClick={() => { plantTile(tile.id); setShowCatalog(false); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${tile.color}25`, borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s', textAlign: 'left' }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${tile.color}10`; (e.currentTarget as HTMLElement).style.borderColor = `${tile.color}50`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)'; (e.currentTarget as HTMLElement).style.borderColor = `${tile.color}25`; }}
              >
                <span style={{ fontSize: 16 }}>{tile.icon}</span>
                <div>
                  <p style={{ fontFamily: 'monospace', fontSize: 9, color: '#F5F0E8', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>{tile.label}</p>
                  <p style={{ fontFamily: 'monospace', fontSize: 7, color: `${tile.color}70`, letterSpacing: '.06em', textTransform: 'uppercase' }}>{SIZE_CONFIG[tile.size].label} · {tile.tags[0]}</p>
                </div>
                <span style={{ marginLeft: 4, fontSize: 14, color: `${tile.color}70` }}>⊕</span>
              </button>
            ))}
            {filteredAvailable.length === 0 && <p style={{ color: 'rgba(212,197,169,0.4)', fontSize: 12, fontStyle: 'italic' }}>All tools planted. Remove a tile to replant.</p>}
          </div>
        </div>
      )}

      {editMode && (
        <div style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 14 }}>⌘</span>
          <p style={{ fontFamily: 'monospace', fontSize: 9, color: 'rgba(201,168,76,0.7)', letterSpacing: '.08em' }}>
            Drag tiles to rearrange · Use size selector to resize · Click ✕ to remove · Click {'</>'} on any tile to get embed code
          </p>
        </div>
      )}

      {/* The canvas grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, alignItems: 'start' }}>
        {plantedTiles.map(tile => (
          <TileCard key={tile.id} tile={tile} editMode={editMode} onRemove={removeTile} onResize={resizeTile} onDragStart={handleDragStart} onDragOver={handleDragOver} onDrop={handleDrop} isDragging={dragging === tile.id} isDragOver={dragOver === tile.id} />
        ))}
        {plantedTiles.length === 0 && (
          <div style={{ gridColumn: 'span 4', textAlign: 'center', padding: '48px 24px', background: 'rgba(255,255,255,0.01)', border: '1px dashed rgba(201,168,76,0.2)', borderRadius: 12 }}>
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.4 }}>⬡</div>
            <p style={{ fontFamily: 'monospace', fontSize: 10, color: 'rgba(212,197,169,0.4)', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 8 }}>Your garden plot is empty</p>
            <button onClick={() => setShowCatalog(true)} style={{ padding: '10px 20px', background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.3)', borderRadius: 8, color, fontFamily: 'monospace', fontSize: 9, letterSpacing: '.1em', textTransform: 'uppercase', cursor: 'pointer' }}>
              ⊕ Plant Your First Tool
            </button>
          </div>
        )}
      </div>

      {/* Canvas footer */}
      <div style={{ marginTop: 16, padding: '12px 16px', background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <p style={{ fontFamily: 'monospace', fontSize: 8, color: 'rgba(212,197,169,0.3)', letterSpacing: '.08em' }}>
          {plantedTiles.length} tiles planted · {availableTiles.length} in catalog · Each tile embeddable on any website
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link href="/asbab-quest" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(212,168,42,0.5)', textDecoration: 'none', padding: '4px 10px', border: '1px solid rgba(212,168,42,0.2)', borderRadius: 4 }}>
            Asbab Quest →
          </Link>
          <Link href="/ward-steward" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(138,138,184,0.5)', textDecoration: 'none', padding: '4px 10px', border: '1px solid rgba(138,138,184,0.2)', borderRadius: 4 }}>
            Ward Steward →
          </Link>
          <Link href="/mrg-embed/collection" style={{ fontFamily: 'monospace', fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: 'rgba(45,122,71,0.5)', textDecoration: 'none', padding: '4px 10px', border: '1px solid rgba(45,122,71,0.2)', borderRadius: 4 }}>
            Embed Tools →
          </Link>
        </div>
      </div>
    </div>
  );
}
